'use client'

import { useMemo } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'

import { getAudioPubList } from '../../services/audio'

import {
  AUDIO_PUB_LIST_KEY,
  DEFAULT_AUDIO_PUB_PAGE_SIZE,
} from './audio-keys'

import type { AudioPubItem } from '../../models/audio'

interface UseAudioPubListOptions {
  tagId?: number
  pageSize?: number
  enabled?: boolean
}

export const useAudioPubList = ({
  tagId,
  pageSize = DEFAULT_AUDIO_PUB_PAGE_SIZE,
  enabled = true,
}: UseAudioPubListOptions = {}) => {
  const query = useInfiniteQuery({
    queryKey: [AUDIO_PUB_LIST_KEY, tagId ?? 0, pageSize],
    enabled,
    queryFn: async ({ pageParam = 0 }) => {
      return getAudioPubList({
        page: pageParam,
        pageSize,
        ...(tagId ? { tag_id: tagId } : {}),
      })
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage.list.length || lastPage.list.length < pageSize) {
        return undefined
      }
      const loaded = (lastPageParam + 1) * pageSize
      if (lastPage.total > 0 && loaded >= lastPage.total) {
        return undefined
      }
      return lastPageParam + 1
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 2,
  })

  // 分页累加并按 id 去重：后端在数据变动 / 页边界重叠时可能返回重复项，
  // 若不去重会导致列表渲染时 React key 冲突（"two children with the same key"）
  const list = useMemo((): AudioPubItem[] => {
    if (!query.data) return []
    const seen = new Set<number>()
    const result: AudioPubItem[] = []
    for (const page of query.data.pages) {
      for (const item of page.list) {
        if (seen.has(item.id)) continue
        seen.add(item.id)
        result.push(item)
      }
    }
    return result
  }, [query.data])

  const total = useMemo(() => {
    return query.data?.pages[0]?.total ?? 0
  }, [query.data])

  return {
    list,
    total,
    query,
  }
}

export type AudioPubListQuery = ReturnType<typeof useAudioPubList>['query']
